import { DndContext, type DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FC } from 'react';
import { useController, useWatch } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

import {
  DotsGridIcon,
  EditDocumentIcon,
  TrashBinIcon,
} from '../../../../../../../assets/icons/index';
import InfoCircle, { InfoCircleVariant } from '../../../../../../../assets/icons/info-circle';
import { SendDocumentIcon } from '../../../../../../../assets/icons/send-document-icon';
import { AppFormattedMessage } from '../../../../../../../components/app-formatted-message/index';
import { Cursor, Tooltip } from '../../../../../../../components/tooltip/index';
import { useLocale } from '../../../../../../../hooks/index';
import {
  GRANTEE,
  type TemplateSignatoryType,
  templateSignatoryTypeTranslation,
} from '../../../../constants/template-signatory-type.constant';
import {
  SigningMode,
  signingModeDescriptionKey,
  signingModeLabelKey,
  TemplateCategory,
} from '../../../../enums/template.const';
import { StepContentProps } from '../../../../types/template.types';
import { SignatoriesCombobox } from '../combobox/index';

type StepTwoProps = Pick<StepContentProps, 'control'>;

const SIGNING_MODE_OPTIONS: {
  icon: FC<{ className?: string; iconColor?: string }>;
  value: SigningMode;
}[] = [
  { value: SigningMode.ALL_AT_ONCE, icon: SendDocumentIcon },
  { value: SigningMode.SET_ORDER, icon: EditDocumentIcon },
];

type SignatoryOrderItemProps = {
  id: TemplateSignatoryType;
  index: number;
  label: string;
  onRemove: (() => void) | null;
};

const SignatoryOrderItem: FC<SignatoryOrderItemProps> = ({ id, index, label, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging, isOver } =
    useSortable({ id });

  return (
    <div
      className={twMerge(
        'flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2',
        isDragging && 'z-50 opacity-80 shadow-md',
        isOver && !isDragging && 'ring-2 ring-brand-500 ring-offset-1',
      )}
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
    >
      <button
        className="flex cursor-grab touch-none items-center text-gray-400 active:cursor-grabbing"
        type="button"
        {...listeners}
        {...attributes}
        aria-label="Drag to reorder"
      >
        <DotsGridIcon className="h-5 w-5" iconColor="#475467" />
      </button>
      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-brand-50">
        <span className="text-xs font-medium text-brand-700">{index + 1}</span>
      </div>
      <span className="flex-1 text-sm font-medium text-gray-700">{label}</span>
      {onRemove != null && (
        <button
          aria-label="Remove"
          className="rounded p-1 text-gray-400 hover:bg-gray-100"
          onClick={onRemove}
          type="button"
        >
          <TrashBinIcon className="h-4 w-4" iconColor="#A4A7AE" />
        </button>
      )}
    </div>
  );
};

export const StepTwo: FC<StepTwoProps> = ({ control }) => {
  const { messagesLocale } = useLocale();
  const category = useWatch({ control, name: 'stepOne.category' });
  const signatories = useWatch({ control, name: 'stepTwo.signatories' }) as TemplateSignatoryType[];
  const signingMode = useWatch({ control, name: 'stepTwo.signingMode' });
  const { field: signingModeField } = useController({ control, name: 'stepTwo.signingMode' });
  const { field: signatoriesField } = useController({ control, name: 'stepTwo.signatories' });
  const isGrantCategory = category === TemplateCategory.GRANT;

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const fromIndex = signatories.indexOf(active.id as TemplateSignatoryType);
    const toIndex = signatories.indexOf(over.id as TemplateSignatoryType);
    if (fromIndex === -1 || toIndex === -1) return;
    signatoriesField.onChange(arrayMove(signatories, fromIndex, toIndex));
  };

  const labelFor = (v: TemplateSignatoryType) => {
    const labelKey = templateSignatoryTypeTranslation[v];
    return labelKey ? messagesLocale[labelKey] : v;
  };

  const handleSigningModeChange = (value: SigningMode) => {
    signingModeField.onChange(value);
  };

  const onRemove = (id: TemplateSignatoryType) => {
    signatoriesField.onChange(signatories.filter((x) => x !== id));
  };

  return (
    <div className="flex h-full flex-col bg-gray-100">
      <div className="flex flex-1 flex-col gap-6 overflow-y-auto rounded-b-xl border-b border-gray-300 bg-white px-6 pb-4 pt-6">
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-1">
            <span className="text-xs text-gray-500">
              <AppFormattedMessage id="SIGNATORIES" />
            </span>
            <Tooltip
              className="w-[480px]"
              content={<AppFormattedMessage id="SIGNATORIES_TOOLTIP" />}
              cursor={Cursor.POINTER}
              placement="top"
            >
              <button
                className="flex shrink-0 items-center justify-center text-gray-400 hover:text-gray-600"
                type="button"
              >
                <InfoCircle
                  className="size-4 text-gray-400 hover:text-gray-600"
                  iconColor="currentColor"
                  variant={InfoCircleVariant.EVENTS}
                />
              </button>
            </Tooltip>
          </div>
          <SignatoriesCombobox control={control} />
        </div>

        <div className="flex flex-col gap-3">
          <span className="text-xs text-gray-500">
            <AppFormattedMessage id="SIGNING_MODE" />
          </span>
          <div className="flex gap-3">
            {SIGNING_MODE_OPTIONS.map(({ value, icon: Icon }) => {
              const isSelected = signingMode === value;
              const labelKey = signingModeLabelKey[value];
              const descKey = signingModeDescriptionKey[value];
              return (
                <button
                  className={twMerge(
                    'flex flex-1 flex-col gap-3 rounded-lg border-2 p-4 text-left transition-colors',
                    isSelected
                      ? 'border-brand-50 bg-brand-25 text-brand-700'
                      : 'border-gray-100 bg-white text-gray-700',
                  )}
                  key={value}
                  onClick={() => handleSigningModeChange(value)}
                  type="button"
                >
                  <Icon
                    className="h-6 w-6 shrink-0"
                    iconColor={isSelected ? '#2565C8' : '#344054'}
                  />
                  <div className="flex flex-col">
                    <span className="text-sm">
                      <AppFormattedMessage id={labelKey} />
                    </span>
                    <span
                      className={twMerge(
                        'text-[10px] leading-4',
                        isSelected ? 'text-brand-600' : 'text-gray-400',
                      )}
                    >
                      <AppFormattedMessage id={descKey} />
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {signingMode === SigningMode.SET_ORDER && signatories.length > 0 && (
          <div className="flex flex-col gap-4">
            <span className="text-xs text-gray-500">
              <AppFormattedMessage id="SIGNATORIES_ORDER" />
            </span>
            <p className="flex items-center rounded-md bg-sunbeam-50 px-3 py-2 text-xs text-gray-800">
              <AppFormattedMessage id="SIGNATORIES_ORDER_DRAG_HINT" />
            </p>
            <DndContext onDragEnd={handleDragEnd} sensors={sensors}>
              <SortableContext items={signatories} strategy={verticalListSortingStrategy}>
                <div className="flex flex-col gap-2">
                  {signatories.map((id, index) => (
                    <SignatoryOrderItem
                      id={id}
                      index={index}
                      key={id}
                      label={labelFor(id)}
                      onRemove={isGrantCategory && id === GRANTEE ? null : () => onRemove(id)}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        )}
      </div>
    </div>
  );
};
